import { useState, useEffect } from 'react';
import { useAuth } from '@/auth/auth-context';
import './home.css';
import adoraCareLogo from '../images/adoracare-logo.png';
import homeBanner from '../images/banner-home.png';
import dentalBanner from '../images/home-dental-banner.jpg';
import dentalQuestionImg from '../images/dental-questions.jpg';
import elevatePatientImg from '../images/elevate-patient-experiences.png';
import taskImg from '../images/task.jpg';
import boostRevenueImg from '../images/boost-revenue.jpg';

import revenueLekageImg from '../images/revenue-lekage.png';
import analyticsBlockImg from '../images/analytics-block.png';

const CLINIC_NAMES = [
  'Partha Dental', 'SmileCare', 'OrthoPlus', 'MediCare Chain', 'HealCare',
  'Apollo OPD', 'MaxHealth', 'Fortis Clinics', 'CureWell', 'DentaPoint',
];

const PRODUCTS = [
  {
    icon: '🧾',
    tag: 'BILLING',
    title: 'Smart Billing & Collections',
    desc: 'Capture every billable event, flag dues, and send payment links — zero revenue leakage, real-time tracking across every clinic.',
    color: '#4f8ef7',
    metric: '',
    metricLabel: 'Revenue tracked',
    wide: true,
  },
  {
    icon: '💬',
    tag: 'WHATSAPP',
    title: 'WhatsApp Automation',
    desc: 'Reminders, follow-ups, and payment links — triggered automatically, zero manual effort.',
    color: '#22d3ee',
    metric: '',
    metricLabel: 'Messages sent',
    wide: false,
  },
  {
    icon: '📊',
    tag: 'ANALYTICS',
    title: 'Owner Analytics',
    desc: 'One live dashboard across every clinic. No Excel, no morning report, no waiting.',
    color: '#a78bfa',
    metric: '',
    metricLabel: 'Clinics live',
    wide: false,
  },
  {
    icon: '🩺',
    tag: 'DOCTOR PORTAL',
    title: 'Doctor Portal',
    desc: 'Schedule, incentive calculations, and daily task visibility — one clean view for every doctor in the network.',
    color: '#34d399',
    metric: '',
    metricLabel: 'Avg. onboarding',
    wide: true,
  },
];

const CAPABILITIES = [
  {
    tag: 'BILLING & REVENUE',
    title: 'Stop losing revenue to manual billing',
    desc: 'Outstanding dues tracked per patient. Collections sent on WhatsApp with a one-tap payment link. Every billable event captured — no manual entry required.',
    bullets: ['Instant bill capture from completed visits', 'WhatsApp payment links', 'Real-time collections dashboard'],
    icon: '💳',
    iconBg: '#0d1e35',
    bars: [80, 60, 92, 48],
    labels: ['Mar', 'Apr', 'May', 'Jun'],
    img: revenueLekageImg,
  },
  {
    tag: 'ANALYTICS',
    title: 'Know your numbers before you sit down',
    desc: 'Owner dashboard shows revenue, collections, no-show rates, and doctor productivity — live. No morning report. No Excel. No waiting.',
    bullets: ['Live cross-clinic dashboard', 'Revenue by doctor & location', 'No-show rate and recovery tracking'],
    icon: '📊',
    iconBg: '#0e0e22',
    bars: [55, 78, 65, 90],
    labels: ['Q1', 'Q2', 'Q3', 'Q4'],
    img: analyticsBlockImg,
    imgShape: 'rounded' as const,
  },
];

const TESTIMONIALS = [
  {
    clinic: 'Multi-location dental group',
    location: 'Hyderabad · 4 locations',
    badge: 'REVENUE VISIBILITY',
    quote: 'We were running on WhatsApp and Excel. AdoraCare gave us real visibility for the first time. Revenue leakage dropped noticeably in the first week.',
    color: '#22d3ee',
    badgeBg: 'rgba(34,211,238,0.08)',
    badgeBorder: 'rgba(34,211,238,0.22)',
  },
  {
    clinic: 'Clinic chain',
    location: 'Bengaluru · 8 clinics',
    badge: 'MULTI-CLINIC DASHBOARD',
    quote: 'Managing 8 locations without a live dashboard was impossible. Now I know which clinic needs attention before my morning tea.',
    color: '#4f8ef7',
    badgeBg: 'rgba(79,142,247,0.08)',
    badgeBorder: 'rgba(79,142,247,0.22)',
  },
  {
    clinic: 'Orthopaedic practice',
    location: 'Mumbai · 2 locations',
    badge: 'FRONT DESK EFFICIENCY',
    quote: 'The billing module alone saved us hours a day. Front-desk staff adapted quickly. Onboarding was fast and smooth.',
    color: '#a78bfa',
    badgeBg: 'rgba(167,139,250,0.08)',
    badgeBorder: 'rgba(167,139,250,0.22)',
  },
];


const PARTICLES = [
  {x:85,y:65,r:2.5,o:0.28},{x:210,y:145,r:1.5,o:0.18},{x:380,y:52,r:2,o:0.22},
  {x:520,y:190,r:1,o:0.14},{x:640,y:78,r:3,o:0.16},{x:790,y:160,r:1.5,o:0.2},
  {x:925,y:38,r:2,o:0.18},{x:1055,y:130,r:1,o:0.13},{x:1185,y:72,r:2.5,o:0.26},
  {x:1325,y:165,r:1.5,o:0.18},{x:150,y:320,r:1,o:0.1},{x:295,y:280,r:2,o:0.16},
  {x:455,y:350,r:1.5,o:0.13},{x:615,y:292,r:2.5,o:0.18},{x:755,y:372,r:1,o:0.1},
  {x:895,y:302,r:2,o:0.14},{x:1035,y:262,r:1.5,o:0.16},{x:1165,y:342,r:2,o:0.12},
  {x:1305,y:298,r:1,o:0.1},{x:105,y:482,r:2,o:0.16},{x:355,y:522,r:1.5,o:0.13},
  {x:555,y:472,r:1,o:0.1},{x:705,y:542,r:2.5,o:0.18},{x:860,y:492,r:1.5,o:0.14},
  {x:1105,y:518,r:2,o:0.16},{x:1408,y:482,r:1,o:0.09},
];


const ACHIEVE_STATS = [
  { value: 'Seamless Onboarding', label: 'Get started quickly without complex setup processes', color: '#22d3ee' },
  { value: 'Effortless Setup', label: 'Move from onboarding to operations without delays',    color: '#4f8ef7' },
  { value: 'Rapid Deployment',  label: 'Launch operations with minimal implementation time', color: '#a78bfa' },
];


export default function Home() {
  const [navScrolled, setNavScrolled] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [tourSubmitted, setTourSubmitted] = useState(false);
  const [testiIdx, setTestiIdx] = useState(1);
  const { user, loading: authLoading } = useAuth();

  // When an already-signed-in user lands on the public marketing page
  // (e.g. by clicking the AdoraCare wordmark in the header before
  // `/api/dashboard/me` has resolved, or via a deep link), the "Log in"
  // CTAs become wrong — they imply they aren't signed in. Swap to a
  // single "Open dashboard" CTA in that case. While auth is still
  // loading we keep the original public-visitor links to avoid a flash
  // of the dashboard CTA for an anonymous visitor; a brief flicker for
  // an actual signed-in user on a public page is acceptable.
  const isAuthed = !authLoading && user !== null;
  const homeCtaHref = isAuthed ? '/dashboard' : '/login';
  const homeCtaLabel = isAuthed ? 'Open dashboard' : 'Log in';

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="Main">

      {/* Announce bar */}
      <div className="announceBar">
        <span className="announcePill">Beta</span>
        AdoraCare is now live — invite-only access for pilot clinics.{' '}
        <a href="#contact" className="announceLink">Request early access →</a>
      </div>

      {/* Header */}
      <header className={`Header${navScrolled ? ' scrolled' : ''}`}>
        <div className="HeaderInner">
          <a href="/" className="Logo">
            <img src={adoraCareLogo} alt="AdoraCare" className="LogoImg" />
          </a>
          <nav className={`Nav${navOpen ? ' open' : ''}`}>
            <a href="#products">Products</a>
            <a href="#features">Features</a>
            <a href="#testimonials">Stories</a>
          </nav>
          <div className="NavActions">
            <a href={homeCtaHref} className="LoginLink">{homeCtaLabel}</a>
            <a href="#contact" className="DemoBtn">Get a demo</a>
          </div>
          <button
            className="Hamburger"
            onClick={() => setNavOpen(o => !o)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="Hero" 
      style={{ backgroundImage: ` 
      url(${homeBanner})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>

        {/* Concentric ring background — eka.care style */}
        <div className="HeroRings" aria-hidden="true">
          <svg viewBox="0 0 1440 820" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMax slice">
            {[140, 270, 400, 530, 660, 790, 920, 1060, 1200].map((r, i) => (
              <circle
                key={r}
                cx="720"
                cy="820"
                r={r}
                stroke={`rgba(130, 90, 255, ${Math.max(0.03, 0.13 - i * 0.012)})`}
                strokeWidth="1"
              />
            ))}
            {/* centre glow */}
            <circle cx="720" cy="820" r="60" fill="rgba(110, 70, 240, 0.07)" />
            <circle cx="720" cy="820" r="28" fill="rgba(140, 100, 255, 0.1)" />
          </svg>
        </div>

        <div className="HeroInner">
          <div className="HeroBadge">
            <span className="heroBadgeDot" />
            AI-powered clinic management
          </div>
          <h1 className="HeroH1">
            The Operating System<br/><span className="GradText"> for Modern Clinics</span>
          </h1>
          <p className="HeroSub">
            Unified patient data across clinics — billing, appointments, insights, <br/> and WhatsApp automation working together.
          </p>
          <div className="HeroCtas">
            <a href="#contact" className="btnPrimary">Get a demo →</a>
            <a href="#products" className="btnSecondary">Explore products ↓</a>
          </div>
        </div>

        {/* Clinic name marquee */}
        <div className="MarqueeSection">
          <p className="MarqueeLabel"></p>
          <div className="MarqueeWrap darkFade">
            <div className="MarqueeTrack">
              {[...CLINIC_NAMES, ...CLINIC_NAMES].map((name, i) => (
                <div key={i} className="MarqueeItem">{name}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blue Glow — Built to Scale */}
      <section className="BlueSection">
        <div className="BlueSectionParticles" aria-hidden="true">
          <svg viewBox="0 0 1440 600" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
            <rect x="820" y="-120" width="90" height="960" rx="45" fill="rgba(100,200,255,0.04)" transform="rotate(-15 1000 300)" />
            <rect x="1020" y="-120" width="45" height="960" rx="22" fill="rgba(100,200,255,0.025)" transform="rotate(-15 1100 300)" />
            {PARTICLES.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r={p.r} fill={`rgba(140,210,255,${p.o})`} />
            ))}
          </svg>
        </div>

        <div className="BlueSectionInner">
          <div className="BlueSectionRight">
            <div className="BlueSectionImgWrap">
              <img src={dentalBanner} alt="Built to scale" className="BlueSectionImg" />
              <div className="BlueSectionImgOverlay" />
            </div>
          </div>

          <div className="BlueSectionLeft">
            <div className="SectionTag" style={{ color: '#60c8f0', borderColor: 'rgba(96,200,240,0.25)', background: 'rgba(96,200,240,0.07)' }}>
              BUILT TO SCALE
            </div>
            <h2 className="BlueSectionH2">
              Built to scale with<br />your clinic network
            </h2>
            <p className="BlueSectionDesc">
             Built for growing healthcare networks — AdoraCare supports every clinic with a single scalable platform, secure architecture, and standardized experience across locations.    </p>
            <div className="BlueSectionStats">
              <div className="BlueStat">
                <span className="BlueStatIcon">🔮</span>
                <div className="BlueStatText">
                  <span className="BlueStatValue">Unified Platform</span>
                  <span className="BlueStatLabel">One system across all clinics</span>
                </div>
              </div>
              <div className="BlueStat">
                <span className="BlueStatIcon">🎯</span>
                <div className="BlueStatText">
                  <span className="BlueStatValue">Rapid Setup</span>
                  <span className="BlueStatLabel">Go live without delays</span>
                </div>
              </div>
            </div>
            <a href="#contact" className="btnPrimary">Get started →</a>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="BentoSection" id="products">
        <div className="ShowcaseRings" aria-hidden="true">
          <svg viewBox="0 0 1440 820" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMax slice">
            {[140, 270, 400, 530, 660, 790, 920, 1060, 1200].map((r, i) => (
              <circle key={r} cx="720" cy="820" r={r}
                stroke={`rgba(130, 90, 255, ${Math.max(0.03, 0.13 - i * 0.012)})`} strokeWidth="1" />
            ))}
            <circle cx="720" cy="820" r="60" fill="rgba(110, 70, 240, 0.07)" />
            <circle cx="720" cy="820" r="28" fill="rgba(140, 100, 255, 0.1)" />
          </svg>
        </div>
        <div className="BentoInner">
          <div className="SectionHead">
            <div className="SectionTag">PRODUCTS</div>
            <h2 className="SectionH2">One Platform — Complete Clinic Operations</h2>
            <p className="SectionSub">Everything your clinic needs to run smoothly, from front desk to owner analytics.</p>
          </div>
          <div className="BentoGrid">
            {PRODUCTS.map(p => (
              <div key={p.tag} className={`BentoCard${p.wide ? ' BentoCard--wide' : ''}`}>
                <div className="BentoCardTop" style={{ background: `linear-gradient(135deg, ${p.color}18 0%, ${p.color}06 100%)` }}>
                  <div className="BentoCardTopGrid" />
                  <div className="BentoIconWrap">
                    <div className="BentoIconGlow" style={{ background: `radial-gradient(ellipse, ${p.color}40 0%, transparent 70%)` }} />
                    <div className="BentoIconBadge" style={{ background: `linear-gradient(135deg, ${p.color}30, ${p.color}16)`, border: `1px solid ${p.color}44`, boxShadow: `0 8px 24px ${p.color}28` }}>
                      <span className="BentoCardIcon">{p.icon}</span>
                    </div>
                  </div>
                </div>
                <div className="BentoCardBody">
                  <span className="BentoCardTag" style={{ color: p.color }}>{p.tag}</span>
                  <div className="BentoCardTitle">{p.title}</div>
                  <div className="BentoCardDesc">{p.desc}</div>
                  <a href="#features" className="BentoCardCta" style={{ color: p.color }}>Learn more →</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Showcase — AI-powered */}
      <section className="Showcase">
        <div className="ShowcaseRings" aria-hidden="true">
          <svg viewBox="0 0 1440 820" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMax slice">
            {[140, 270, 400, 530, 660, 790, 920, 1060, 1200].map((r, i) => (
              <circle
                key={r}
                cx="720"
                cy="820"
                r={r}
                stroke={`rgba(130, 90, 255, ${Math.max(0.03, 0.13 - i * 0.012)})`}
                strokeWidth="1"
              />
            ))}
            <circle cx="720" cy="820" r="60" fill="rgba(110, 70, 240, 0.07)" />
            <circle cx="720" cy="820" r="28" fill="rgba(140, 100, 255, 0.1)" />
          </svg>
        </div>

        <div className="ShowcaseInner">
          <div className="ShowcaseHead">
            <div className="SectionTag" style={{ color: '#a78bfa', borderColor: 'rgba(167,139,250,0.25)', background: 'rgba(167,139,250,0.07)' }}>
              PLATFORM OVERVIEW
            </div>
            <h2>Achieve More <span className="ShowcaseGrad">with AdoraCare</span></h2>
            <p className="SectionSub">Patients, Doctors, Billing, WhatsApp — one intelligent system built for clinics of every size.</p>
          </div>

          <div className="ShowcaseGrid ShowcaseGrid--3">
            <div className="ShowcaseCard">
              <div className="ShowcaseCardImg">
                <img src={elevatePatientImg} alt="Elevate patient experiences" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
              </div>
              <div className="ShowcaseCardBody">
                <span className="ShowcaseTag">Patient experience</span>
                <h3>Elevate every patient interaction from walk-in to follow-up.</h3>
                <p>Streamlined check-ins, instant billing, and automated follow-ups — so patients feel attended to at every step.</p>
                <a href="#contact" className="ShowcaseLearn">See how it works →</a>
              </div>
            </div>

            <div className="ShowcaseCard">
              <div className="ShowcaseCardImg">
                <img src={taskImg} alt="Front desk tasks" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
              </div>
              <div className="ShowcaseCardBody">
                <span className="ShowcaseTag">Front desk flow</span>
                <h3>Patient walks in. Bill sent on WhatsApp. Done in under a minute.</h3>
                <p>Front desk staff log visits, check availability, and trigger payment links — all from WhatsApp. No new app, no training overhead.</p>
                <a href="#contact" className="ShowcaseLearn">See the front desk flow →</a>
              </div>
            </div>

            <div className="ShowcaseCard">
              <div className="ShowcaseCardImg">
                <img src={boostRevenueImg} alt="Boost revenue" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
              </div>
              <div className="ShowcaseCardBody">
                <span className="ShowcaseTag">Revenue</span>
                <h3>Stop losing revenue to missed bills and uncollected dues.</h3>
                <p>Every billable event captured, every outstanding due tracked, and payment links sent automatically — zero leakage.</p>
                <a href="#features" className="ShowcaseLearn">Explore revenue tools →</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Get a Tour CTA — below showcase */}
      <section className="CtaSection">
        <div className="CtaCard">
          <div className="CtaSideImg CtaSideImg--left">
            <img src={dentalQuestionImg} alt="" />
            <div className="CtaSideImgFade CtaSideImgFade--left" />
          </div>
          <div className="CtaGlow" aria-hidden="true" />
          <div className="CtaContent">
            <span className="CtaPill">GET STARTED</span>
            <p className="CtaSub">Still have questions?</p>
        
            <p className="CtaDesc">
              We will address your remaining questions, demonstrate key features
              in a live environment, and provide the technical clarity you need.
            </p>
            {tourSubmitted ? (
              <div className="TourSuccess">✅ Thanks! We'll reach out on WhatsApp within 24 hours.</div>
            ) : (
              <form
                className="CtaForm"
                onSubmit={e => { e.preventDefault(); setTourSubmitted(true); }}
              >
                <input placeholder="Your name" required />
                <input placeholder="Phone (WhatsApp)" type="tel" required />
                <input placeholder="Clinic name" required />
                <select defaultValue="">
                  <option value="" disabled>Specialty</option>
                  <option>Dental</option>
                  <option>Orthopaedics</option>
                  <option>Physiotherapy</option>
                  <option>Dermatology</option>
                  <option>General Medicine</option>
                  <option>Other</option>
                </select>
                <button type="submit" className="btnPrimary" style={{ fontSize: '1rem', padding: '14px 32px', border: 'none', cursor: 'pointer' }}>
                  Get in touch →
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="Section" id="features">
        <div className="CapList">
          {CAPABILITIES.map((cap, i) => (
            <div key={i} className={`CapRow${i % 2 === 1 ? ' reversed' : ''}`}>
              <div className="CapContent">
                <div className="SectionTag">{cap.tag}</div>
                <h3 className="CapTitle">{cap.title}</h3>
                <p className="CapDesc">{cap.desc}</p>
                <ul className="CapBullets">
                  {cap.bullets.map(b => (
                    <li key={b}>
                      <span className="CapCheck">✓</span>
                      {b}
                    </li>
                  ))}
                </ul>
                <a href="#contact" className="btnPrimary" style={{ marginTop: 22, display: 'inline-flex' }}>
                  Get started →
                </a>
              </div>
              <div className="CapVisual">
                {'img' in cap && cap.img ? (
                  <div className="CapVisualCircleWrap">
                    <img
                      src={cap.img as string}
                      alt={cap.title}
                      className={'imgShape' in cap && cap.imgShape === 'rounded' ? 'CapVisualRoundImg' : 'CapVisualCircleImg'}
                    />
                  </div>
                ) : (
                  <div className="CapVisualInner">
                    <span className="CapVisualIcon">{cap.icon}</span>
                    {cap.bars.map((w, j) => (
                      <div key={j} className="CapMockRow">
                        <div className="CapMockLabel">{cap.labels[j]}</div>
                        <div className="CapMockBar" style={{ width: `${w}%` }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="TestiSection" id="testimonials">
        <div className="TestiSectionInner">
          <div className="SectionHead">
            <div className="SectionTag" style={{ color: '#a78bfa', borderColor: 'rgba(167,139,250,0.25)', background: 'rgba(167,139,250,0.07)' }}>TESTIMONIALS</div>
            <h2 className="SectionH2" style={{ color: '#fff' }}>Real Stories — Measurable Impact</h2>
            <p className="SectionSub" style={{ color: 'rgba(255,255,255,0.42)' }}>Clinic owners and COCs share what changed after switching to AdoraCare.</p>
          </div>
          <div className="TestiCarousel">
            <button
              className="TestiNav"
              onClick={() => setTestiIdx(i => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
              aria-label="Previous"
            >‹</button>

            <div className="TestiSlider">
              {[-1, 0, 1].map(offset => {
                const idx = (testiIdx + offset + TESTIMONIALS.length) % TESTIMONIALS.length;
                const t = TESTIMONIALS[idx];
                const isActive = offset === 0;
                return (
                  <div
                    key={idx}
                    className={`TestiCard${isActive ? ' TestiCard--active' : ' TestiCard--side'}`}
                    onClick={() => !isActive && setTestiIdx(idx)}
                  >
                    <div className="TestiAccentBar" style={{ background: `linear-gradient(90deg, ${t.color}, ${t.color}55)` }} />
                    <div className="TestiBody">
                      <div className="TestiStars">{'★★★★★'}</div>
                      <span className="TestiQuoteMark">"</span>
                      <p className="TestiQuote">{t.quote}</p>
                      <div className="TestiClinic">{t.clinic}</div>
                      <div className="TestiLocation">{t.location}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              className="TestiNav"
              onClick={() => setTestiIdx(i => (i + 1) % TESTIMONIALS.length)}
              aria-label="Next"
            >›</button>
          </div>

          <div className="TestiDots">
            {TESTIMONIALS.map((_, i) => (
              <button key={i} className={`TestiDot${i === testiIdx ? ' active' : ''}`} onClick={() => setTestiIdx(i)} />
            ))}
          </div>
        </div>
      </section>

      {/* Integrations + Trust */}
      <section className="Section">
        <div className="SectionHead">
          <div className="SectionTag">IMPACT</div>
          <h2 className="SectionH2">From Complexity to Control and Growth</h2>
          <p className="SectionSub">AdoraCare turns fragmented clinic operations into a single, intelligent system — so every location runs with clarity.</p>
        </div>
        <div className="AchieveStats" style={{ margin: '48px 0 52px', gap: 16, border: 'none', background: 'none', overflow: 'visible', borderRadius: 0 }}>
          {ACHIEVE_STATS.map(s => (
            <div key={s.label} className="AchieveStat" style={{ background: 'linear-gradient(135deg, #0a0a2e, #0d1850)', borderRadius: 20, border: '1px solid rgba(120,80,255,0.22)', borderRight: '1px solid rgba(120,80,255,0.22)' }}>
              <span
                className="AchieveStatValue"
                style={{ background: `linear-gradient(135deg, ${s.color}, #fff)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
              >
                {s.value}
              </span>
              <span className="AchieveStatLabel">{s.label}</span>
            </div>
          ))}
        </div>
      </section>


      {/* Footer */}
      <footer className="Footer">
        <div className="FooterInner">
          <div>
            <a href="/" className="FooterLogo">
              <img src={adoraCareLogo} alt="AdoraCare" className="LogoImg LogoImgFooter" />
            </a>
            <p className="FooterTagline">
              The specialty-agnostic clinic OS for India and the US.
            </p>
            <div className="FooterCopy">© 2026 AdoraCare · adoracare.ai</div>
          </div>
          <div className="FooterCol">
            <div className="FooterColHead">Product</div>
            <a href="#products">Products</a>
            <a href="#features">Features</a>
            <a href="#testimonials">Stories</a>
          </div>
          <div className="FooterCol">
            <div className="FooterColHead">Access</div>
            <a href="#contact">Request demo</a>
            {isAuthed ? (
              <a href="/dashboard">Open dashboard</a>
            ) : (
              <>
                <a href="/login">Owner login</a>
                <a href="/staff-login">Staff login</a>
              </>
            )}
            <a href="/walkin">Walk-in form</a>
          </div>
          <div className="FooterCol">
            <div className="FooterColHead">Company</div>
            <a href="#">About</a>
            <a href="#">Privacy policy</a>
            <a href="#">Terms of use</a>
          </div>
        </div>
      </footer>

    </div>
  );
}
